import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import JSZip from 'jszip';
import { ArrowLeft, Check, CheckCircle2, ChevronDown, ChevronUp, Download, Images, Lock, Smartphone, Sparkles, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PUBLISHING_FONTS_URL as GOOGLE_FONTS_URL, PUBLISHING_FONT_OPTIONS as FONT_OPTIONS, PUBLISHING_GRADIENTS as BACKGROUND_GRADIENTS } from '../lib/publishingDesignOptions';

const OUTPUT_SIZES = [
  { id: 'ios-iphone-6-9', platform: 'iOS', device: 'iPhone 6.9-inch', width: 1320, height: 2868, folder: 'ios/iphone-6.9-inch' },
  { id: 'ios-iphone-6-7', platform: 'iOS', device: 'iPhone 6.7-inch', width: 1290, height: 2796, folder: 'ios/iphone-6.7-inch' },
  { id: 'ios-ipad-13', platform: 'iOS', device: 'iPad 13-inch', width: 2064, height: 2752, folder: 'ios/ipad-13-inch' },
  { id: 'android-phone', platform: 'Android', device: 'Phone', width: 1080, height: 1920, folder: 'android/phone' },
  { id: 'android-tablet-7', platform: 'Android', device: '7-inch tablet', width: 1200, height: 1920, folder: 'android/tablet-7-inch' },
  { id: 'android-tablet-10', platform: 'Android', device: '10-inch tablet', width: 1600, height: 2560, folder: 'android/tablet-10-inch' },
];

const safeBaseName = (name) => {
  const withoutExtension = name.replace(/\.[^.]+$/, '');
  return withoutExtension.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'screenshot';
};

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not create the output image.'))), 'image/png');
});

const addWatermark = (context, width, height) => {
  const text = 'allsmalltools.com';
  const fontSize = Math.max(18, Math.round(width * 0.018));
  const paddingX = Math.round(fontSize * 0.65);
  const paddingY = Math.round(fontSize * 0.4);
  const margin = Math.round(fontSize * 0.8);
  context.save();
  context.font = `600 ${fontSize}px Arial, sans-serif`;
  context.textBaseline = 'middle';
  const textWidth = context.measureText(text).width;
  const boxWidth = textWidth + paddingX * 2;
  const boxHeight = fontSize + paddingY * 2;
  const x = width - boxWidth - margin;
  const y = height - boxHeight - margin;
  context.fillStyle = 'rgba(17, 24, 39, 0.62)';
  context.fillRect(x, y, boxWidth, boxHeight);
  context.fillStyle = '#ffffff';
  context.fillText(text, x + paddingX, y + boxHeight / 2);
  context.restore();
};

const roundedRect = (context, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
};

const wrapText = (context, text, maxWidth) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = words[0];
  for (let index = 1; index < words.length; index += 1) {
    const test = `${line} ${words[index]}`;
    if (context.measureText(test).width <= maxWidth) line = test;
    else {
      lines.push(line);
      line = words[index];
    }
  }
  lines.push(line);
  return lines;
};

const drawPromotionalText = (context, headline, supportingText, width, height, textColor, fontFamily) => {
  const maxWidth = width * 0.88;
  const headerHeight = height * 0.29;
  let fontSize = Math.round(Math.min(width * 0.095, height * 0.065));
  let lines = [];
  do {
    context.font = `700 ${fontSize}px "${fontFamily}", sans-serif`;
    lines = wrapText(context, headline, maxWidth);
    if (lines.length <= 3 && lines.length * fontSize * 1.03 <= headerHeight * 0.62) break;
    fontSize -= 2;
  } while (fontSize > Math.round(width * 0.038));

  context.save();
  context.fillStyle = textColor;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `700 ${fontSize}px "${fontFamily}", sans-serif`;
  const lineHeight = fontSize * 1.03;
  const blockHeight = lines.length * lineHeight;
  const startY = height * 0.045 + lineHeight / 2;
  lines.forEach((line, index) => context.fillText(line, width / 2, startY + index * lineHeight, maxWidth));

  if (supportingText.trim()) {
    let supportingSize = Math.round(Math.min(width * 0.035, height * 0.026));
    context.font = `600 ${supportingSize}px "${fontFamily}", sans-serif`;
    let supportingLines = wrapText(context, supportingText.toUpperCase(), maxWidth);
    while (supportingLines.length > 2 && supportingSize > 18) {
      supportingSize -= 2;
      context.font = `600 ${supportingSize}px "${fontFamily}", sans-serif`;
      supportingLines = wrapText(context, supportingText.toUpperCase(), maxWidth);
    }
    const supportingLineHeight = supportingSize * 1.25;
    const supportingY = Math.max(startY + blockHeight + supportingSize * 0.6, headerHeight * 0.75);
    context.globalAlpha = 0.82;
    supportingLines.slice(0, 2).forEach((line, index) => context.fillText(line, width / 2, supportingY + index * supportingLineHeight, maxWidth));
  }
  context.restore();
};

const drawBackground = (context, width, height, backgroundStyle, backgroundColor) => {
  if (backgroundStyle === 'solid') {
    context.fillStyle = backgroundColor;
  } else {
    const selected = BACKGROUND_GRADIENTS.find(({ id }) => id === backgroundStyle) || BACKGROUND_GRADIENTS[0];
    const gradient = context.createLinearGradient(0, 0, width, height);
    selected.colors.forEach((color, index) => gradient.addColorStop(index / (selected.colors.length - 1), color));
    context.fillStyle = gradient;
  }
  context.fillRect(0, 0, width, height);
};

const drawDeviceFrame = (context, image, sourceWidth, sourceHeight, width, height, platform, device, fitMode) => {
  const frameWidth = width * (device.toLowerCase().includes('tablet') || device.toLowerCase().includes('ipad') ? 0.84 : 0.76);
  const border = Math.max(12, Math.round(frameWidth * 0.024));
  const screenWidth = frameWidth - border * 2;
  const screenHeight = screenWidth * (height / width);
  const frameHeight = screenHeight + border * 2;
  const x = (width - frameWidth) / 2;
  const y = height * 0.29;
  const outerRadius = Math.min(frameWidth * 0.13, 90);
  const screenRadius = Math.max(outerRadius - border, 18);

  context.save();
  context.shadowColor = 'rgba(0, 0, 0, 0.28)';
  context.shadowBlur = Math.round(width * 0.025);
  context.shadowOffsetY = Math.round(width * 0.012);
  roundedRect(context, x, y, frameWidth, frameHeight, outerRadius);
  if (platform === 'iOS') {
    const titanium = context.createLinearGradient(x, y, x + frameWidth, y + frameHeight);
    titanium.addColorStop(0, '#d8dce2');
    titanium.addColorStop(0.2, '#6b7280');
    titanium.addColorStop(0.52, '#f3f4f6');
    titanium.addColorStop(0.78, '#525966');
    titanium.addColorStop(1, '#c9ced6');
    context.fillStyle = titanium;
  } else {
    context.fillStyle = '#09090b';
  }
  context.fill();
  context.restore();

  if (platform === 'iOS') {
    const rim = Math.max(4, Math.round(frameWidth * 0.007));
    context.save();
    roundedRect(context, x + rim, y + rim, frameWidth - rim * 2, frameHeight - rim * 2, Math.max(outerRadius - rim, 1));
    context.fillStyle = '#08090b';
    context.fill();
    context.restore();

    context.save();
    context.fillStyle = '#5f6672';
    const buttonWidth = Math.max(5, Math.round(frameWidth * 0.009));
    const buttonRadius = buttonWidth / 2;
    if (!device.toLowerCase().includes('ipad')) {
      roundedRect(context, x - buttonWidth * 0.65, y + frameHeight * 0.2, buttonWidth, frameHeight * 0.1, buttonRadius);
      context.fill();
      roundedRect(context, x - buttonWidth * 0.65, y + frameHeight * 0.33, buttonWidth, frameHeight * 0.075, buttonRadius);
      context.fill();
      roundedRect(context, x + frameWidth - buttonWidth * 0.35, y + frameHeight * 0.25, buttonWidth, frameHeight * 0.15, buttonRadius);
      context.fill();
    } else {
      roundedRect(context, x + frameWidth * 0.73, y - buttonWidth * 0.35, frameWidth * 0.11, buttonWidth, buttonRadius);
      context.fill();
    }
    context.restore();
  }

  const screenX = x + border;
  const screenY = y + border;
  context.save();
  roundedRect(context, screenX, screenY, screenWidth, screenHeight, screenRadius);
  context.clip();
  context.fillStyle = '#ffffff';
  context.fillRect(screenX, screenY, screenWidth, screenHeight);
  const scale = fitMode === 'cover'
    ? Math.max(screenWidth / sourceWidth, screenHeight / sourceHeight)
    : Math.min(screenWidth / sourceWidth, screenHeight / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  context.drawImage(image, screenX + (screenWidth - drawWidth) / 2, screenY + (screenHeight - drawHeight) / 2, drawWidth, drawHeight);
  context.restore();

  const isTablet = device.toLowerCase().includes('tablet') || device.toLowerCase().includes('ipad');
  context.fillStyle = '#050505';
  if (platform === 'iOS' && !isTablet) {
    const islandWidth = screenWidth * 0.25;
    const islandHeight = Math.max(border * 1.38, screenWidth * 0.043);
    const islandX = width / 2 - islandWidth / 2;
    const islandY = screenY + border * 0.48;
    context.save();
    context.shadowColor = 'rgba(0, 0, 0, 0.45)';
    context.shadowBlur = Math.max(3, border * 0.35);
    roundedRect(context, islandX, islandY, islandWidth, islandHeight, islandHeight / 2);
    context.fill();
    context.restore();
    context.beginPath();
    context.fillStyle = '#172033';
    context.arc(islandX + islandWidth * 0.79, islandY + islandHeight / 2, islandHeight * 0.19, 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.fillStyle = 'rgba(68, 104, 151, 0.72)';
    context.arc(islandX + islandWidth * 0.81, islandY + islandHeight * 0.43, islandHeight * 0.07, 0, Math.PI * 2);
    context.fill();
  } else if (platform === 'iOS') {
    context.beginPath();
    context.fillStyle = '#101216';
    context.arc(width / 2, screenY + Math.max(border * 0.72, 9), Math.max(4, border * 0.22), 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.fillStyle = '#263852';
    context.arc(width / 2 + Math.max(1, border * 0.04), screenY + Math.max(border * 0.68, 8), Math.max(1.5, border * 0.07), 0, Math.PI * 2);
    context.fill();
  } else {
    context.beginPath();
    context.arc(width / 2, screenY + Math.max(border * 0.75, 9), Math.max(4, border * 0.28), 0, Math.PI * 2);
    context.fill();
  }
};

const loadImage = async (file) => {
  if ('createImageBitmap' in window) return window.createImageBitmap(file);

  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not read ${file.name}.`));
    };
    image.src = url;
  });
};

const AppScreenshotGeneratorPage = () => {
  const inputRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const screenshotsRef = useRef([]);
  const [screenshots, setScreenshots] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState(() => OUTPUT_SIZES.map(({ id }) => id));
  const [previewSizeId, setPreviewSizeId] = useState(OUTPUT_SIZES[0].id);
  const [fitMode, setFitMode] = useState('contain');
  const [background, setBackground] = useState('#0f56c5');
  const [backgroundStyle, setBackgroundStyle] = useState('ocean');
  const [textColor, setTextColor] = useState('#ffffff');
  const [fontFamily, setFontFamily] = useState('Poppins');
  const [dragging, setDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [fontLibraryReady, setFontLibraryReady] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  screenshotsRef.current = screenshots;
  useEffect(() => () => screenshotsRef.current.forEach(({ preview }) => URL.revokeObjectURL(preview)), []);
  useEffect(() => {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = GOOGLE_FONTS_URL;
    stylesheet.dataset.appScreenshotFonts = 'true';
    stylesheet.onload = () => setFontLibraryReady(true);
    stylesheet.onerror = () => {
      setFontLibraryReady(true);
      setError('The font library could not be loaded. System fallbacks will be used until the connection is restored.');
    };
    document.head.appendChild(stylesheet);
    return () => stylesheet.remove();
  }, []);

  useEffect(() => {
    const firstScreenshot = screenshots[0];
    const size = OUTPUT_SIZES.find(({ id }) => id === previewSizeId);
    const canvas = previewCanvasRef.current;
    if (!firstScreenshot || !size || !canvas || !fontLibraryReady) return undefined;

    let cancelled = false;
    let loadedImage;
    const renderPreview = async () => {
      try {
        await document.fonts.load(`700 64px "${fontFamily}"`);
        await document.fonts.load(`600 32px "${fontFamily}"`);
        loadedImage = await loadImage(firstScreenshot.file);
        if (cancelled) {
          if (typeof loadedImage.close === 'function') loadedImage.close();
          return;
        }
        const sourceWidth = loadedImage.width || loadedImage.naturalWidth;
        const sourceHeight = loadedImage.height || loadedImage.naturalHeight;
        const isLandscape = sourceWidth > sourceHeight;
        const width = isLandscape ? size.height : size.width;
        const height = isLandscape ? size.width : size.height;
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        drawBackground(context, width, height, backgroundStyle, background);
        drawPromotionalText(
          context,
          firstScreenshot.headline.trim() || 'Your Headline Here',
          firstScreenshot.supportingText.trim() || 'Add supporting text for this screenshot',
          width,
          height,
          textColor,
          fontFamily
        );
        drawDeviceFrame(context, loadedImage, sourceWidth, sourceHeight, width, height, size.platform, size.device, fitMode);
        addWatermark(context, width, height);
      } catch (previewError) {
        if (!cancelled) setError(previewError.message || 'The preview could not be rendered.');
      }
    };

    renderPreview();
    return () => {
      cancelled = true;
      if (loadedImage && typeof loadedImage.close === 'function') loadedImage.close();
    };
  }, [screenshots, previewSizeId, fitMode, background, backgroundStyle, textColor, fontFamily, fontLibraryReady]);

  const addFiles = (fileList) => {
    const imageFiles = Array.from(fileList || []).filter((file) => ['image/png', 'image/jpeg'].includes(file.type));
    if (!imageFiles.length) {
      setError('Choose one or more PNG or JPEG screenshots.');
      return;
    }

    setScreenshots((current) => [
      ...current,
      ...imageFiles.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        headline: '',
        supportingText: '',
      })),
    ]);
    setError('');
  };

  const updateScreenshotText = (id, field, value) => {
    setScreenshots((current) => current.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeScreenshot = (id) => {
    setScreenshots((current) => {
      const removed = current.find((item) => item.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return current.filter((item) => item.id !== id);
    });
  };

  const toggleSize = (id) => {
    setSelectedSizes((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const generateZip = async () => {
    if (!screenshots.length || !selectedSizes.length) {
      setError('Add at least one screenshot and select at least one output size.');
      return;
    }
    if (screenshots.some(({ headline }) => !headline.trim())) {
      setError('Add a headline for every uploaded screenshot.');
      return;
    }

    setIsGenerating(true);
    setError('');
    const zip = new JSZip();
    const sizes = OUTPUT_SIZES.filter(({ id }) => selectedSizes.includes(id));
    const total = screenshots.length * sizes.length;
    let completed = 0;

    try {
      setProgress(`Loading ${fontFamily}…`);
      await document.fonts.load(`700 64px "${fontFamily}"`);
      await document.fonts.load(`600 32px "${fontFamily}"`);

      for (let fileIndex = 0; fileIndex < screenshots.length; fileIndex += 1) {
        const { file, headline, supportingText } = screenshots[fileIndex];
        const image = await loadImage(file);
        const sourceWidth = image.width || image.naturalWidth;
        const sourceHeight = image.height || image.naturalHeight;
        const isLandscape = sourceWidth > sourceHeight;

        for (const size of sizes) {
          const width = isLandscape ? size.height : size.width;
          const height = isLandscape ? size.width : size.height;
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          drawBackground(context, width, height, backgroundStyle, background);
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          drawPromotionalText(context, headline, supportingText, width, height, textColor, fontFamily);
          drawDeviceFrame(context, image, sourceWidth, sourceHeight, width, height, size.platform, size.device, fitMode);
          addWatermark(context, width, height);

          const blob = await canvasToBlob(canvas);
          const suffix = String(fileIndex + 1).padStart(2, '0');
          zip.file(`${size.folder}/${safeBaseName(file.name)}-${suffix}-${width}x${height}.png`, blob);
          completed += 1;
          setProgress(`Creating image ${completed} of ${total}`);
          await new Promise((resolve) => window.setTimeout(resolve, 0));
        }

        if (typeof image.close === 'function') image.close();
      }

      setProgress('Building ZIP file…');
      const archive = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const url = URL.createObjectURL(archive);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'allsmalltools-app-screenshots.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setProgress(`Downloaded ${total} store-ready images`);
      setShowDownloadModal(true);
    } catch (generationError) {
      setError(generationError.message || 'The screenshots could not be generated. Please try again.');
      setProgress('');
    } finally {
      setIsGenerating(false);
    }
  };

  const faqs = [
    { question: 'Are my screenshots uploaded?', answer: 'No. Your screenshots are resized and packaged entirely inside your browser. They are never sent to the AllSmallTools server.' },
    { question: 'Which image formats can I upload?', answer: 'You can upload PNG and JPEG screenshots. Generated store screenshots are exported as non-transparent PNG files.' },
    { question: 'How are iOS and Android frames selected?', answer: 'The generator automatically draws an iPhone or iPad-style frame for iOS outputs and an Android-style frame for Android outputs. No external device-frame images are required.' },
    { question: 'Are the font choices free to use?', answer: 'Yes. The font menu uses open-source Google Fonts. The selected font file is loaded for rendering, while your screenshot and promotional text remain in your browser.' },
  ];

  return (
    <>
      <Helmet>
        <title>App Screenshot Generator for iOS & Android | AllSmallTooLS</title>
        <meta name="description" content="Turn app screenshots into promotional iPhone, iPad, Android phone, and tablet images with headlines, device frames, and ZIP download." />
        <meta name="keywords" content="app screenshot generator, app store screenshots, google play screenshots, iOS screenshot sizes, Android screenshot sizes" />
        <link rel="canonical" href="https://allsmalltools.com/app-screenshot-generator" />
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Back to Home</Button></Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2"><Images className="h-8 w-8 text-indigo-600" /><span>App Screenshot Generator</span></h1>
            <p className="text-gray-600">Prepare screenshots for iOS and Android phones and tablets</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader><CardTitle>Upload App Screenshots</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
                onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}
                className={`w-full min-h-56 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/50'}`}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-indigo-500" />
                <span className="block text-lg font-semibold text-gray-900">Drop screenshots here or click to browse</span>
                <span className="block text-sm text-gray-500 mt-2">Upload multiple PNG or JPEG files, then add text for each one</span>
              </button>
              <input ref={inputRef} type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} />

              {screenshots.length > 0 && (
                <div className="space-y-4">
                  {screenshots.map((item) => (
                    <div key={item.id} className="relative grid grid-cols-[88px_1fr] gap-4 rounded-xl border bg-gray-50 p-3">
                      <div>
                        <img src={item.preview} alt={`Preview of ${item.file.name}`} className="h-28 w-full rounded bg-white object-contain" />
                        <p className="mt-1 truncate text-xs text-gray-600" title={item.file.name}>{item.file.name}</p>
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label htmlFor={`headline-${item.id}`} className="text-xs">Headline <span className="text-red-600">*</span></Label>
                          <Input id={`headline-${item.id}`} value={item.headline} maxLength={80} onChange={(event) => updateScreenshotText(item.id, 'headline', event.target.value)} placeholder="e.g. Discover Products Fast" />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`supporting-${item.id}`} className="text-xs">Supporting Text <span className="font-normal text-gray-400">(optional)</span></Label>
                          <Input id={`supporting-${item.id}`} value={item.supportingText} maxLength={120} onChange={(event) => updateScreenshotText(item.id, 'supportingText', event.target.value)} placeholder="e.g. Browse everything instantly" />
                        </div>
                      </div>
                      <button type="button" onClick={() => removeScreenshot(item.id)} className="absolute -right-2 -top-2 rounded-full bg-gray-900 p-1 text-white shadow" aria-label={`Remove ${item.file.name}`}><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Output Settings</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {['iOS', 'Android'].map((platform) => (
                <fieldset key={platform} className="space-y-3">
                  <legend className="font-semibold text-gray-900">{platform} sizes</legend>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {OUTPUT_SIZES.filter((size) => size.platform === platform).map((size) => (
                      <label key={size.id} className="flex min-w-0 cursor-pointer items-start gap-2 rounded-lg border border-gray-200 p-2.5 hover:border-indigo-300 hover:bg-indigo-50">
                        <input type="checkbox" checked={selectedSizes.includes(size.id)} onChange={() => toggleSize(size.id)} className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-indigo-600" />
                        <span className="min-w-0"><span className="block text-xs font-medium leading-tight text-gray-900">{size.device}</span><span className="mt-1 block whitespace-nowrap text-[11px] leading-tight text-gray-500">{size.width} × {size.height}px</span></span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Promotional Font</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="h-11 w-full justify-between px-3 font-normal" aria-label="Choose promotional font">
                        <span className="truncate text-base" style={{ fontFamily: `"${fontFamily}", sans-serif` }}>{fontFamily}</span>
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="max-h-80 w-72 overflow-y-auto p-1">
                      {FONT_OPTIONS.map((font) => (
                        <DropdownMenuItem key={font} onSelect={() => setFontFamily(font)} className="flex cursor-pointer items-center justify-between py-2.5">
                          <span className="text-lg" style={{ fontFamily: `"${font}", sans-serif` }}>{font}</span>
                          {fontFamily === font && <Check className="h-4 w-4 text-indigo-600" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <p className="text-xs text-gray-500">45 free, open-source font choices</p>
                </div>
                <div className="space-y-2">
                  <Label>Background</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="h-11 w-full justify-between gap-3 px-3 font-normal" aria-label="Choose background style">
                        {backgroundStyle === 'solid' ? (
                          <><span className="h-6 w-12 flex-shrink-0 rounded border" style={{ backgroundColor: background }} /><span className="flex-1 text-left">Solid Color</span></>
                        ) : (() => {
                          const selectedGradient = BACKGROUND_GRADIENTS.find(({ id }) => id === backgroundStyle) || BACKGROUND_GRADIENTS[0];
                          return <><span className="h-6 w-12 flex-shrink-0 rounded" style={{ background: `linear-gradient(135deg, ${selectedGradient.colors.join(', ')})` }} /><span className="flex-1 truncate text-left">{selectedGradient.name}</span></>;
                        })()}
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-72 p-1">
                      <DropdownMenuItem onSelect={() => setBackgroundStyle('solid')} className="flex cursor-pointer items-center gap-3 py-2.5">
                        <span className="h-8 w-16 rounded border" style={{ backgroundColor: background }} />
                        <span className="flex-1">Solid Color</span>
                        {backgroundStyle === 'solid' && <Check className="h-4 w-4 text-indigo-600" />}
                      </DropdownMenuItem>
                      {BACKGROUND_GRADIENTS.map((gradient) => (
                        <DropdownMenuItem key={gradient.id} onSelect={() => setBackgroundStyle(gradient.id)} className="flex cursor-pointer items-center gap-3 py-2.5">
                          <span className="h-8 w-16 rounded" style={{ background: `linear-gradient(135deg, ${gradient.colors.join(', ')})` }} />
                          <span className="flex-1">{gradient.name}</span>
                          {backgroundStyle === gradient.id && <Check className="h-4 w-4 text-indigo-600" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <p className="text-xs text-gray-500">10 visual gradients plus a solid color</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fit-mode">Image Placement</Label>
                  <select id="fit-mode" value={fitMode} onChange={(event) => setFitMode(event.target.value)} className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="contain">Fit with background — recommended</option>
                    <option value="cover">Crop to fill</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="screenshot-background">Background Color</Label>
                  {backgroundStyle === 'solid' ? <div className="flex gap-2"><Input id="screenshot-background" type="color" value={background} onChange={(event) => setBackground(event.target.value)} className="w-14 p-1" /><Input value={background} onChange={(event) => setBackground(event.target.value)} aria-label="Background color value" /></div> : <div className="h-10 flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-xs text-gray-500">Used for solid mode</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="screenshot-text-color">Text Color</Label>
                  <div className="flex gap-2"><Input id="screenshot-text-color" type="color" value={textColor} onChange={(event) => setTextColor(event.target.value)} className="w-14 p-1" /><Input value={textColor} onChange={(event) => setTextColor(event.target.value)} aria-label="Text color value" /></div>
                </div>
              </div>

              <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
                iOS exports use an iPhone/iPad-style frame. Android exports use an Android-style frame. The screenshot is clipped safely inside the selected frame.
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
                <p className="font-semibold">{screenshots.length || 0} screenshot{screenshots.length === 1 ? '' : 's'} × {selectedSizes.length} size{selectedSizes.length === 1 ? '' : 's'}</p>
                <p className="mt-1">Your ZIP will contain {screenshots.length * selectedSizes.length} PNG image{screenshots.length * selectedSizes.length === 1 ? '' : 's'}, organized by platform and device.</p>
              </div>

              {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
              {progress && !error && <p className="flex items-center gap-2 text-sm text-green-700"><CheckCircle2 className="h-5 w-5" />{progress}</p>}
              <Button onClick={generateZip} disabled={isGenerating || !fontLibraryReady || !screenshots.length || !selectedSizes.length} className="w-full bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700">
                <Download className="h-4 w-4 mr-2" />{!fontLibraryReady ? 'Loading Font Library…' : isGenerating ? progress || 'Generating…' : 'Generate & Download ZIP'}
              </Button>
              <p className="flex items-center justify-center gap-2 text-xs text-gray-500"><Lock className="h-4 w-4" />Private browser processing — no uploads</p>
            </CardContent>
          </Card>
        </div>

        {screenshots.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle>Live Preview — First Screenshot</CardTitle>
                <span className="text-sm text-gray-500">Choose any output size to preview</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3" role="group" aria-label="Preview output size">
                {OUTPUT_SIZES.map((size) => {
                  const isActive = previewSizeId === size.id;
                  return (
                    <button
                      key={size.id}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setPreviewSizeId(size.id)}
                      className={`rounded-md border px-2.5 py-1.5 text-left transition-colors ${isActive ? 'border-indigo-600 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-600' : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/60'}`}
                    >
                      <span className="block text-xs font-semibold leading-tight">{size.platform} · {size.device}</span>
                      <span className={`mt-0.5 block text-[10px] leading-tight ${isActive ? 'text-indigo-600' : 'text-gray-500'}`}>{size.width} × {size.height}px</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex min-h-72 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-[linear-gradient(45deg,#f3f4f6_25%,transparent_25%),linear-gradient(-45deg,#f3f4f6_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#f3f4f6_75%),linear-gradient(-45deg,transparent_75%,#f3f4f6_75%)] bg-[length:24px_24px] bg-[position:0_0,0_12px,12px_-12px,-12px_0px] p-4 sm:p-6">
                <canvas ref={previewCanvasRef} className="max-h-[640px] max-w-full rounded-lg shadow-2xl" aria-label="Live preview of the first generated promotional screenshot" />
              </div>
              <p className="text-center text-xs text-gray-500">Preview size selection does not change which sizes are included in the downloaded ZIP.</p>
            </CardContent>
          </Card>
        )}

        <Card className="mt-12">
          <CardHeader><h2 className="text-2xl font-bold text-gray-900">Why Use Our App Screenshot Generator?</h2></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[[Sparkles, 'Promotional Layouts', 'Combine per-image text, 45 free fonts, 10 gradients, and clean device frames for every screenshot.'], [Lock, 'Private by Design', 'Every screenshot is processed locally in your browser and never uploaded to a server.'], [Download, 'One Organized ZIP', 'Download all generated PNG files in clearly named platform and device folders.']].map(([Icon, title, description]) => (
                <div key={title} className="flex flex-col items-start space-y-3"><div className="bg-indigo-100 p-3 rounded-lg"><Icon className="h-6 w-6 text-indigo-700" /></div><h3 className="text-lg font-semibold text-gray-900">{title}</h3><p className="text-gray-600">{description}</p></div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg"><p className="text-gray-700">Use screenshots captured from the matching device type whenever possible. Resizing can prepare the file dimensions, but it cannot turn a phone interface into an authentic tablet layout.</p></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-xl font-bold text-gray-900">How to Generate App Store Screenshots</h2></CardHeader>
          <CardContent><ol className="space-y-4 text-gray-700">{[['Upload Screenshots', 'Add one or more PNG or JPEG captures from your app.'], ['Add Promotional Text', 'Enter a headline and optional supporting text for every uploaded screenshot.'], ['Choose Store Sizes and Style', 'Select iOS and Android outputs, a font, gradient, image placement, and text color.'], ['Download the ZIP', 'Generate framed promotional images and download one organized ZIP package.']].map(([title, description], index) => <li key={title} className="flex items-start space-x-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold">{index + 1}</span><div><strong>{title}:</strong> {description}</div></li>)}</ol></CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{faqs.map((faq, index) => <div key={faq.question} className="border border-gray-200 rounded-lg overflow-hidden"><button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors" aria-expanded={openFaq === index}><h3 className="font-semibold text-gray-900">{faq.question}</h3>{openFaq === index ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}</button>{openFaq === index && <div className="p-4 bg-white"><p className="text-gray-700">{faq.answer}</p></div>}</div>)}</div></CardContent>
        </Card>
      </div>

      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="items-center text-center">
            <div className="mb-2 rounded-full bg-green-100 p-3"><CheckCircle2 className="h-8 w-8 text-green-600" /></div>
            <DialogTitle className="text-2xl">Thank You!</DialogTitle>
            <DialogDescription className="text-center text-base">Your app screenshots are being downloaded in an organized ZIP file.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-indigo-50 p-4 text-center">
            <p className="font-semibold text-gray-900">Publishing on Google Play?</p>
            <p className="mt-1 text-sm text-gray-600">A 1024 × 500 feature graphic is generally required for a complete Play Store listing.</p>
          </div>
          <Button asChild className="h-11 w-full bg-gradient-to-r from-fuchsia-500 to-indigo-600 hover:from-fuchsia-600 hover:to-indigo-700">
            <Link to="/feature-graphic-generator" onClick={() => setShowDownloadModal(false)}><Sparkles className="h-4 w-4" />Create a Feature Graphic</Link>
          </Button>
          <Button variant="ghost" onClick={() => setShowDownloadModal(false)} className="w-full">Maybe Later</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppScreenshotGeneratorPage;
